import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { getProductsByFilters, getBrandsList, getSpecifications } from '../api/productApi';
import { getCategories } from '../api/categoryApi';

const AllProducts = ({ onCartUpdate }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper to parse array params
  const getParamArray = (key) => {
    const val = searchParams.get(key);
    return val ? val.split(',').filter(Boolean) : [];
  };

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [sortBy, setSortBy] = useState('relevance');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');

  // Category from URL (already handling default 'all')
  const categorySlug = searchParams.get('category') || 'all';

  // Filter states - Initialize from URL
  const [priceRange, setPriceRange] = useState(() => {
    const min = Number(searchParams.get('minPrice')) || 0;
    const max = Number(searchParams.get('maxPrice')) || 200000;
    return [min, max];
  });

  const [selectedBrands, setSelectedBrands] = useState(() => getParamArray('brand'));
  const [selectedRAM, setSelectedRAM] = useState(() => getParamArray('ram'));
  const [selectedStorage, setSelectedStorage] = useState(() => getParamArray('storage'));
  const [selectedProcessor, setSelectedProcessor] = useState(() => getParamArray('processor'));
  const [selectedScreen, setSelectedScreen] = useState(() => getParamArray('screenSize'));

  // Dynamic filter options from backend
  const [brands, setBrands] = useState([]);
  const [ramOptions, setRamOptions] = useState([]);
  const [storageOptions, setStorageOptions] = useState([]);
  const [processorOptions, setProcessorOptions] = useState([]);
  const [screenOptions, setScreenOptions] = useState([]);

  // Categories list
  const [categories, setCategories] = useState([]);

  // Fetch filter options & categories once
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [brandsRes, specsRes, catRes] = await Promise.all([
          getBrandsList(),
          getSpecifications(),
          getCategories({ isActive: true })
        ]);

        if (brandsRes?.success && Array.isArray(brandsRes.data)) {
          setBrands(brandsRes.data.map(b => b.name).filter(Boolean));
        }

        if (specsRes?.success && specsRes.data) {
          setRamOptions(specsRes.data.ram || []);
          setStorageOptions(specsRes.data.storage || []);
          setProcessorOptions(specsRes.data.processor || []);
          setScreenOptions(specsRes.data.screenSize || []);
        }

        if (catRes?.success && catRes.data?.categories) {
          const catList = catRes.data.categories.map(cat => ({
            id: cat._id,
            name: cat.name,
            slug: cat.slug || cat.name.toLowerCase().replace(/\s+/g, '-')
          }));
          setCategories(catList);
        }
      } catch (err) {
        console.error('Failed to load filter options/categories:', err);
      }
    };

    fetchOptions();
  }, []);

  // Sync search query with URL (for back button support)
  useEffect(() => {
    const q = searchParams.get('search');
    if (q !== searchQuery) {
      setSearchQuery(q || '');
    }
  }, [searchParams, searchQuery]);

  // Sync URL changes to state (essential for links from Header when already on the page)
  useEffect(() => {
    const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    
    const qBrand = getParamArray('brand');
    const qRam = getParamArray('ram');
    const qStorage = getParamArray('storage');
    const qProcessor = getParamArray('processor');
    const qScreen = getParamArray('screenSize');
    
    // Use functional state updates to avoid stale closures
    setSelectedBrands(prev => !isEqual(qBrand, prev) ? qBrand : prev);
    setSelectedRAM(prev => !isEqual(qRam, prev) ? qRam : prev);
    setSelectedStorage(prev => !isEqual(qStorage, prev) ? qStorage : prev);
    setSelectedProcessor(prev => !isEqual(qProcessor, prev) ? qProcessor : prev);
    setSelectedScreen(prev => !isEqual(qScreen, prev) ? qScreen : prev);
    
    const min = Number(searchParams.get('minPrice')) || 0;
    const max = Number(searchParams.get('maxPrice')) || 200000;
    
    setPriceRange(prev => {
      if (prev[0] !== min || prev[1] !== max) {
        return [min, max];
      }
      return prev;
    });
  }, [searchParams]);

  // Reset page when filters/sort/category/search change
  useEffect(() => {
    setPage(1);
  }, [
    sortBy, searchQuery, priceRange, selectedBrands,
    selectedRAM, selectedStorage, selectedProcessor, selectedScreen,
    categorySlug
  ]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        page,
        limit,
        isActive: 'true',
        sort: sortBy
      };

      // Category filter
      if (categorySlug && categorySlug !== 'all') {
        filters.category = categorySlug;
      }

      // Other filters
      if (priceRange[0] > 0) filters.minPrice = priceRange[0];
      if (priceRange[1] < 200000) filters.maxPrice = priceRange[1];
      if (selectedBrands.length) filters.brand = selectedBrands.join(',');
      if (selectedRAM.length) filters.ram = selectedRAM.join(',');
      if (selectedStorage.length) filters.storage = selectedStorage.join(',');
      if (selectedProcessor.length) filters.processor = selectedProcessor.join(',');
      if (selectedScreen.length) filters.screenSize = selectedScreen.join(',');
      if (searchQuery.trim()) filters.search = searchQuery.trim();

      const response = await getProductsByFilters(filters);

      if (response.success) {
        setProducts(response.data.products || []);

        const total = response.pagination?.total || 0;
        setTotalProducts(total);
        setTotalPages(Math.ceil(total / limit));
      } else {
        setProducts([]);
        setTotalProducts(0);
        setTotalPages(1);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch products');
      setProducts([]);
      setTotalProducts(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    page, limit, sortBy, categorySlug,
    priceRange, selectedBrands, selectedRAM,
    selectedStorage, selectedProcessor, selectedScreen,
    searchQuery
  ]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const removeFilter = (type, value = null) => {
    let paramKey = type;
    if (type === 'brand') { setSelectedBrands(prev => prev.filter(b => b !== value)); paramKey = 'brand'; }
    if (type === 'ram') { setSelectedRAM(prev => prev.filter(r => r !== value)); paramKey = 'ram'; }
    if (type === 'storage') { setSelectedStorage(prev => prev.filter(s => s !== value)); paramKey = 'storage'; }
    if (type === 'processor') { setSelectedProcessor(prev => prev.filter(p => p !== value)); paramKey = 'processor'; }
    if (type === 'screen') { setSelectedScreen(prev => prev.filter(s => s !== value)); paramKey = 'screenSize'; }
    
    if (['brand', 'ram', 'storage', 'processor', 'screen'].includes(type)) {
      const newParams = new URLSearchParams(searchParams);
      const currentList = newParams.get(paramKey)?.split(',').filter(Boolean) || [];
      const updatedList = currentList.filter(item => item !== value);
      if (updatedList.length > 0) newParams.set(paramKey, updatedList.join(','));
      else newParams.delete(paramKey);
      setSearchParams(newParams);
    }

    if (type === 'search') {
      setSearchQuery('');
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('search');
      setSearchParams(newParams);
    }
    if (type === 'price') {
      setPriceRange([0, 200000]);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('minPrice');
      newParams.delete('maxPrice');
      setSearchParams(newParams);
    }
  };

  const clearAllFilters = () => {
    setPriceRange([0, 200000]);
    setSelectedBrands([]);
    setSelectedRAM([]);
    setSelectedStorage([]);
    setSelectedProcessor([]);
    setSelectedScreen([]);
    setSearchQuery('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    newParams.delete('minPrice');
    newParams.delete('maxPrice');
    newParams.delete('brand');
    newParams.delete('ram');
    newParams.delete('storage');
    newParams.delete('processor');
    newParams.delete('screenSize');
    setSearchParams(newParams);
  };

  const handleCategoryChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', value);
    }
    setSearchParams(newParams);
  };

  const handlePriceChange = (newRange) => {
    setPriceRange(newRange);
    const newParams = new URLSearchParams(searchParams);
    if (newRange[0] > 0) newParams.set('minPrice', newRange[0]);
    else newParams.delete('minPrice');
    
    if (newRange[1] < 200000) newParams.set('maxPrice', newRange[1]);
    else newParams.delete('maxPrice');
    
    setSearchParams(newParams);
  };

  const FilterSection = ({ title, options, selected, setSelected, paramKey }) => (
    <div className="mb-6">
      <h3 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">{title}</h3>
      <div className="space-y-2">
        {options.map((option) => (
          <div key={option} className="flex items-center space-x-2">
            <Checkbox
              checked={selected.includes(option)}
              onCheckedChange={(checked) => {
                let newSelected;
                if (checked) newSelected = [...selected, option];
                else newSelected = selected.filter(item => item !== option);
                setSelected(newSelected);
                
                const newParams = new URLSearchParams(searchParams);
                if (newSelected.length > 0) newParams.set(paramKey, newSelected.join(','));
                else newParams.delete(paramKey);
                setSearchParams(newParams);
              }}
            />
            <label className="text-sm cursor-pointer text-gray-700 dark:text-gray-300">{option}</label>
          </div>
        ))}
      </div>
    </div>
  );

  const PaginationControls = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          disabled={page === 1 || loading}
          onClick={() => setPage(p => Math.max(1, p - 1))}
        >
          Previous
        </Button>

        <div className="flex items-center gap-4 mx-4">
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <span className="text-sm text-muted-foreground">
            ({totalProducts} products)
          </span>
        </div>

        <Button
          variant="outline"
          size="sm"
          disabled={page === totalPages || loading}
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
        >
          Next
        </Button>
      </div>
    );
  };

  return (
    <div className="py-12 bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="container mx-auto px-4">

        <h1 className="text-4xl font-bold mb-6 text-gray-900 dark:text-gray-100">{(() => {
          const minP = Number(searchParams.get('minPrice')) || 0;
          const maxP = Number(searchParams.get('maxPrice')) || 0;
          if (maxP > 0 && minP === 0) return `Best Devices Under ₹${maxP.toLocaleString('en-IN')}`;
          if (minP > 0 && maxP === 0) return `Best Devices Above ₹${minP.toLocaleString('en-IN')}`;
          if (minP > 0 && maxP > 0)   return `Best Devices ₹${minP.toLocaleString('en-IN')} – ₹${maxP.toLocaleString('en-IN')}`;
          return 'All Products';
        })()}</h1>

        {/* Search, Category, Sort */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-transparent dark:border-gray-700 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Mobile Filters Trigger */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <div className="mb-6">
                    <h3 className="font-semibold mb-3">Price Range</h3>
                    <Slider
                      min={0}
                      max={200000}
                      step={1000}
                      value={priceRange}
                      onValueChange={handlePriceChange}
                    />
                  </div>
                  <FilterSection title="Brand" options={brands} selected={selectedBrands} setSelected={setSelectedBrands} paramKey="brand" />
                  <FilterSection title="RAM" options={ramOptions} selected={selectedRAM} setSelected={setSelectedRAM} paramKey="ram" />
                  <FilterSection title="Storage" options={storageOptions} selected={selectedStorage} setSelected={setSelectedStorage} paramKey="storage" />
                  <FilterSection title="Processor" options={processorOptions} selected={selectedProcessor} setSelected={setSelectedProcessor} paramKey="processor" />
                  <FilterSection title="Screen Size" options={screenOptions} selected={selectedScreen} setSelected={setSelectedScreen} paramKey="screenSize" />
                  <Button onClick={clearAllFilters} variant="outline" className="w-full mt-4">
                    Clear All Filters
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                const newParams = new URLSearchParams(searchParams);
                if (e.target.value.trim()) {
                  newParams.set('search', e.target.value.trim());
                } else {
                  newParams.delete('search');
                }
                setSearchParams(newParams);
              }}
              className="flex-1"
            />

            <Select value={categorySlug} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.slug}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">Relevance</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="discount">Best Discount</SelectItem>
                <SelectItem value="newest">Newest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Applied Filters */}
        {(selectedBrands.length ||
          selectedRAM.length ||
          selectedStorage.length ||
          selectedProcessor.length ||
          selectedScreen.length ||
          searchQuery.trim() ||
          priceRange[0] > 0 ||
          priceRange[1] < 200000) && (
            <div className="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-sm border border-transparent dark:border-gray-700 mb-6">
              <div className="flex flex-wrap gap-2">
                {searchQuery.trim() && (
                  <Badge className="flex items-center gap-2">
                    Search: {searchQuery}
                    <X size={14} onClick={() => removeFilter('search')} />
                  </Badge>
                )}

                {(priceRange[0] > 0 || priceRange[1] < 200000) && (
                  <Badge className="flex items-center gap-2">
                    ₹{priceRange[0].toLocaleString()} – ₹{priceRange[1].toLocaleString()}
                    <X size={14} onClick={() => removeFilter('price')} />
                  </Badge>
                )}

                {selectedBrands.map(b => (
                  <Badge key={b} className="flex items-center gap-2">
                    {b} <X size={14} onClick={() => removeFilter('brand', b)} />
                  </Badge>
                ))}

                {selectedRAM.map(r => (
                  <Badge key={r} className="flex items-center gap-2">
                    {r} <X size={14} onClick={() => removeFilter('ram', r)} />
                  </Badge>
                ))}

                {selectedStorage.map(s => (
                  <Badge key={s} className="flex items-center gap-2">
                    {s} <X size={14} onClick={() => removeFilter('storage', s)} />
                  </Badge>
                ))}

                {selectedProcessor.map(p => (
                  <Badge key={p} className="flex items-center gap-2">
                    {p} <X size={14} onClick={() => removeFilter('processor', p)} />
                  </Badge>
                ))}

                {selectedScreen.map(s => (
                  <Badge key={s} className="flex items-center gap-2">
                    {s} <X size={14} onClick={() => removeFilter('screen', s)} />
                  </Badge>
                ))}

                <Button size="sm" variant="ghost" onClick={clearAllFilters}>
                  Clear All
                </Button>
              </div>
            </div>
          )}

        <div className="flex gap-6">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-64 bg-white dark:bg-gray-900 border border-transparent dark:border-gray-700 p-6 rounded-lg shadow-sm sticky top-20 h-fit">
            <h2 className="font-bold mb-4 text-gray-900 dark:text-gray-100">Filters</h2>
<div className="mb-6">
  <h3 className="font-semibold mb-3">Price Range</h3>

  {/* Selected Range Display */}
  <div className="flex items-center gap-2 mb-3">
    <div className="flex-1 border dark:border-gray-600 rounded px-3 py-1 text-sm text-center bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
      ₹{priceRange[0].toLocaleString()}
    </div>
    <span className="text-gray-500 dark:text-gray-400">–</span>
    <div className="flex-1 border dark:border-gray-600 rounded px-3 py-1 text-sm text-center bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
      ₹{priceRange[1].toLocaleString()}
    </div>
  </div>

  <Slider
    min={0}
    max={200000}
    step={1000}
    value={priceRange}
    onValueChange={handlePriceChange}
  />

  {/* Absolute Limits */}
  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
    <span>₹0</span>
    <span>₹2,00,000</span>
  </div>
</div>

            <FilterSection title="Brand" options={brands} selected={selectedBrands} setSelected={setSelectedBrands} paramKey="brand" />
            <FilterSection title="RAM" options={ramOptions} selected={selectedRAM} setSelected={setSelectedRAM} paramKey="ram" />
            <FilterSection title="Storage" options={storageOptions} selected={selectedStorage} setSelected={setSelectedStorage} paramKey="storage" />
            <FilterSection title="Processor" options={processorOptions} selected={selectedProcessor} setSelected={setSelectedProcessor} paramKey="processor" />
            <FilterSection title="Screen Size" options={screenOptions} selected={selectedScreen} setSelected={setSelectedScreen} paramKey="screenSize" />
          </div>

          {/* Products Area */}
          <div className="flex-1">
            {loading ? (
              <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-lg text-gray-500 dark:text-gray-400">Loading...</div>
            ) : error ? (
              <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-lg text-red-500">{error}</div>
            ) : products.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {products.map(product => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      onAddToCart={onCartUpdate}
                    />
                  ))}
                </div>

                <PaginationControls />
              </>
            ) : (
              <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-lg text-gray-500 dark:text-gray-400">
                No products found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllProducts;